import * as THREE from "three";

// The Artemis II replay's scene objects (artemis-mission.js gives the
// numbers): the whole path, bright where Orion has been and faint ahead, and
// Orion itself, a small model kept a readable size on screen with a glow
// that stays visible from across the Earth–Moon distance. Everything sits in
// a group laid out in the mission's inertial frame and turned into the
// Earth-fixed scene each frame.

const PATH_SAMPLES = 2400;
const PATH_COLOUR = new THREE.Color(0.55, 0.85, 1.0);
// Orion's model spans this fraction of its distance from the camera, and its
// glow this fraction of the screen height.
const MODEL_SCREEN_SIZE = 0.035;
const GLOW_SCREEN_SIZE = 0.05;

const PATH_VERTEX = /* glsl */ `
  attribute float aU;
  varying float vU;
  void main() {
    vU = aU;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const PATH_FRAGMENT = /* glsl */ `
  uniform vec3 uColour;
  uniform float uNow;
  varying float vU;
  void main() {
    // Travelled: bright, brightest just behind Orion. Ahead: a faint guide.
    float behind = uNow - vU;
    float alpha = behind >= 0.0 ? 0.55 + 0.45 * exp(-behind / 2.0) : 0.18;
    gl_FragColor = vec4(uColour, alpha);
  }
`;

function glowTexture(inner, outer) {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d");
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, inner);
  g.addColorStop(0.25, outer);
  g.addColorStop(1, "rgba(0, 0, 0, 0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// Orion, one unit long along +X (its direction of travel): the crew module's
// cone ahead of the service module, with its four solar wings in an X.
function buildOrion() {
  const model = new THREE.Group();
  const hull = new THREE.MeshStandardMaterial({ color: 0xe8e8e2, roughness: 0.6, metalness: 0.2 });
  const panel = new THREE.MeshStandardMaterial({ color: 0x2a3550, roughness: 0.35, metalness: 0.6, side: THREE.DoubleSide });
  const crew = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.28, 0.3, 20), hull);
  crew.rotation.z = -Math.PI / 2;
  crew.position.x = 0.3;
  const service = new THREE.Mesh(new THREE.CylinderGeometry(0.27, 0.27, 0.45, 20), hull);
  service.rotation.z = -Math.PI / 2;
  service.position.x = -0.08;
  model.add(crew, service);
  for (let i = 0; i < 4; i++) {
    const wing = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.9), panel);
    const a = Math.PI / 4 + (i * Math.PI) / 2;
    wing.position.set(-0.15, Math.cos(a) * 0.72, Math.sin(a) * 0.72);
    wing.rotation.x = -a;
    model.add(wing);
  }
  return { model, materials: [hull, panel] };
}

export function createMissionScene(scene, mission) {
  const group = new THREE.Group();
  group.renderOrder = 3;

  // The whole path, sampled evenly in playback time so the slow parts
  // (launch, flyby, re-entry) are as smooth as the coasts.
  const positions = new Float32Array(PATH_SAMPLES * 3);
  const us = new Float32Array(PATH_SAMPLES);
  const state = {};
  for (let i = 0; i < PATH_SAMPLES; i++) {
    const u = (i / (PATH_SAMPLES - 1)) * mission.playbackSeconds;
    mission.stateAt(mission.timeAt(u), state);
    positions.set([state.inertial.x, state.inertial.y, state.inertial.z], i * 3);
    us[i] = u;
  }
  const pathGeometry = new THREE.BufferGeometry();
  pathGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  pathGeometry.setAttribute("aU", new THREE.BufferAttribute(us, 1));
  const pathMaterial = new THREE.ShaderMaterial({
    uniforms: { uColour: { value: PATH_COLOUR }, uNow: { value: 0 } },
    vertexShader: PATH_VERTEX,
    fragmentShader: PATH_FRAGMENT,
    transparent: true,
    depthWrite: false,
  });
  const path = new THREE.Line(pathGeometry, pathMaterial);
  path.frustumCulled = false;
  path.renderOrder = 3;
  group.add(path);

  const { model, materials } = buildOrion();
  group.add(model);

  const glow = new THREE.Sprite(new THREE.SpriteMaterial({
    map: glowTexture("rgba(255, 255, 255, 1)", "rgba(150, 210, 255, 0.35)"),
    blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: false, toneMapped: false,
  }));
  glow.scale.setScalar(GLOW_SCREEN_SIZE);
  glow.renderOrder = 4;
  group.add(glow);

  // The ascent's exhaust: a bright plume behind the rocket until MECO.
  const meco = mission.milestones.find((m) => m.id === "meco").t;
  const flame = new THREE.Sprite(new THREE.SpriteMaterial({
    map: glowTexture("rgba(255, 245, 220, 1)", "rgba(255, 150, 60, 0.5)"),
    blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: false, toneMapped: false,
  }));
  flame.renderOrder = 4;
  group.add(flame);

  scene.add(group);

  const ahead = {};
  const forward = new THREE.Vector3();
  const up = new THREE.Vector3();
  const side = new THREE.Vector3();
  const basis = new THREE.Matrix4();

  return {
    /** Places everything for mission state `s` (artemis-mission stateAt) at playback position `u`. */
    update(s, u, camera) {
      group.rotation.y = s.frameAngle;
      pathMaterial.uniforms.uNow.value = u;
      model.position.copy(s.inertial);
      glow.position.copy(s.inertial);

      // Point along the direction of travel, wings square to the Earth.
      mission.stateAt(s.ms + 60_000, ahead);
      forward.subVectors(ahead.inertial, s.inertial);
      if (forward.lengthSq() > 1e-12) {
        forward.normalize();
        up.copy(s.inertial).normalize();
        side.crossVectors(forward, up);
        if (side.lengthSq() > 1e-8) {
          side.normalize();
          up.crossVectors(side, forward);
          basis.makeBasis(forward, up, side);
          model.quaternion.setFromRotationMatrix(basis);
        }
      }
      const distance = camera.position.distanceTo(s.position);
      model.scale.setScalar(Math.max(0.05, distance * MODEL_SCREEN_SIZE));

      const burning = s.t < meco;
      flame.visible = burning;
      if (burning) {
        flame.position.copy(s.inertial).addScaledVector(forward, -distance * MODEL_SCREEN_SIZE * 0.8);
        flame.scale.setScalar(GLOW_SCREEN_SIZE * (1.2 + 0.25 * Math.sin(performance.now() / 45)));
      }
    },
    dispose() {
      scene.remove(group);
      pathGeometry.dispose();
      pathMaterial.dispose();
      model.traverse((o) => o.geometry?.dispose());
      materials.forEach((m) => m.dispose());
      for (const s of [glow, flame]) { s.material.map.dispose(); s.material.dispose(); }
    },
  };
}

/**
 * How far the camera should sit from Orion: close near the Earth and the
 * Moon so the surface is in frame, pulled back in deep space to show the
 * path. `moonPosition` is the drawn Moon's centre.
 */
export function framingDistance(position, globeRadius, moonPosition, moonRadius) {
  const fromEarth = position.length() - globeRadius;
  const fromMoon = position.distanceTo(moonPosition) - moonRadius;
  return Math.min(260, Math.max(14, Math.min(fromEarth * 1.2 + 14, fromMoon * 1.6 + 12)));
}
